import React, { useCallback, useMemo, useState, useEffect, memo } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import AdvancedSearchVehiclesModal from './AdvancedSearchVehiclesModal';
import ConsigneeInfo from './ConsigneeInfo';
import FileUpload from '../../../../components/FileUpload';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { forEach } from 'lodash';

/* ---------- Utils (pure) ---------- */
const parseWeightToKg = (w) => {
  if (w == null) return 0;
  if (typeof w === 'number') return w;
  const s = String(w).trim().toLowerCase();
  const num = parseFloat(s.replace(/[^0-9.-]/g, '')) || 0;
  const isLb = /lb|lbs|pound/.test(s);
  return isLb ? num * 0.45359237 : num; // assume kg if no unit
};
const formatKg = (kg) => `${new Intl.NumberFormat().format(Math.round(kg))} kg`;





/* ---------- Row (memo) ---------- */
const VehicleRow = memo(function VehicleRow({ v, disabled, onDelete, t }) {
  return (
    <ListItem
      secondaryAction={
        <Tooltip title={t('Delete')}>
          <span>
            <IconButton edge="end" color="error" size="small" onClick={() => onDelete(v.id)} disabled={disabled}>
              <DeleteOutlineIcon />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 36, height: 36, fontSize: 18 }}>
          <DirectionsCarFilledIcon fontSize="inherit" />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: '#0B3C5D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={` ${v.year} ${v.make?.name} ${v.model?.name}`}
          >
            {`${v?.vehicleType?.name} ${v.year} ${v.make?.name} ${v.model?.name}`}
          </Typography>
        }
        secondary={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} divider={<span style={{ width: 6 }} />} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" variant="outlined" label={`${t('VIN')}: ${v.vin}`} />
            <Chip size="small" variant="outlined" label={`${t('Weight')}: ${v.weight}`} />
            <Chip size="small" color="success" variant="outlined" label={`${t('Price')}: ${v.price}`} />
          </Stack>
        }
      />
    </ListItem>
  );
});

// Helper to read nested path (containers[0].shipmentItems)
const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  const norm = path.replace(/\[(\w+)\]/g, '.$1').replace(/\['([^']+)'\]/g, '.$1');
  return norm.split('.').filter(Boolean).reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const toFormikDotPath = (path) => path.replace(/\[(\w+)\]/g, '.$1').replace(/\['([^']+)'\]/g, '.$1');

function VehiclesInfo({ disabled, basePath = '', compact = false, title , index }) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [listVehiclesNotShowing, setListVehiclesNotShowing] = useState([]);
  const formik = useFormikContext();
  const { values, setFieldValue } = formik;
  const shipmentItemsPath = basePath ? `${basePath}.shipmentItems` : 'shipmentItems';
  const netWeightPath = basePath ? `${basePath}.netWeight` : 'netWeight';
  const shipmentItems = useMemo(() => getByPath(values, shipmentItemsPath) || [], [values, shipmentItemsPath]);
  const vehicles = useMemo(() => (shipmentItems || []).map((item) => item?.vehicle).filter(Boolean), [shipmentItems]);
  // primaryClient now stored at shipment root, not per container
  const canOpenSelector = Boolean(formik.values?.primaryClient) && Boolean(values?.booking) && !disabled;

  const totalNetWeightKg = useMemo(
    () => (Array.isArray(vehicles) ? vehicles : []).reduce((sum, v) => sum + parseWeightToKg(v?.weight), 0),
    [vehicles]
  );



  const openDialog = () => {


const containers = formik.values.containers || [];



let vehicle = [];

forEach(containers, (c, i) => {
  if (i !== index) {
    vehicle = [...vehicle, ...(c.shipmentItems || []).map((item) => item?.vehicle).filter(Boolean)];
  }
});

setListVehiclesNotShowing(vehicle);


    setDialogOpen(true);
  };

  // Keep form field in sync without loops
  useEffect(() => {
    const currentNet = getByPath(values, netWeightPath) ?? 0;
    if (currentNet !== totalNetWeightKg) {
      setFieldValue(netWeightPath, totalNetWeightKg, false);
    }
  }, [totalNetWeightKg, values, netWeightPath, setFieldValue]);

  const handleDelete = useCallback(
    (id) => {
      const next = shipmentItems.filter((item) => item?.vehicle?.id !== id);
      setFieldValue(shipmentItemsPath, next, false);
    },
    [shipmentItems, setFieldValue, shipmentItemsPath]
  );

  return (
    <Box sx={{ pt: compact ? 0 : 4 }}>
      {!compact && (
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0B3C5D', mb: 1 }}>
          {title || t('Vehicles Information')}
        </Typography>
      )}
      {compact && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#0B3C5D' }}>
          {title || t('Vehicles')}
        </Typography>
      )}

      {/* Actions bar */}
    <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'stretch', md: 'space-between' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 1.5,
      px: compact ? 0 : { xs: 0, md: 3 },
      pt: compact ? 0 : { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 360 } }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => openDialog()}
            startIcon={<DirectionsCarFilledIcon />}
            disabled={!canOpenSelector}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {t('Select Vehicle')}
          </Button>
          {!canOpenSelector && (
            <Typography variant="caption" color="error">
              {t('Please select a client and booking number first')}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip label={`${t('Total')}: ${vehicles.length}`} color="default" variant="outlined" sx={{ fontWeight: 600 }} />
          <Chip
            label={`${t('Net Weight')}: ${formatKg(totalNetWeightKg)}`}
            color="info"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </Box>

      {/* Vehicles list */}
  <Box sx={{ px: compact ? 0 : { xs: 0, md: 3 }, mt: 2 }}>
        {shipmentItems.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center', color: 'text.secondary' }}>
            {t('No vehicles added yet')}
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e5eaef', bgcolor: '#fff' }}>
            <List
              disablePadding
              sx={{
                width: '100%',
                '& .MuiListItem-root': { px: 1.5, py: 1.25 },
              }}
            >
              {shipmentItems.map((item, idx) => {
                const vehicle = item?.vehicle;
                if (!vehicle) return null;
                return (
                  <React.Fragment key={vehicle.id || vehicle.vin || idx}>
                    <VehicleRow v={vehicle} disabled={disabled} onDelete={handleDelete} t={t} />
                    {formik.values.shipmentMode === 'LCL' && (
                      <Box sx={{ px: 3, pb: 2, pt: 1, bgcolor: '#f7f9fb' }}>
                        <FileUpload
                          disabled={disabled}
                          label={t('Bill of Lading File')}
                          formikName={`${toFormikDotPath(shipmentItemsPath)}.${idx}.billOfLadingFile`}
                          uploadName="BillOfLadingFile"
                          accept=".pdf"
                        />
                      </Box>
                    )}
                    {formik.values.shipmentMode === 'SPL' && (
                      <Box sx={{ px: 3, pb: 2, pt: 1, bgcolor: '#f7f9fb' }}>
                        <ConsigneeInfo
                          disabled={disabled}
                          basePath={`${shipmentItemsPath}[${idx}]`}
                          compact
                          fullDetailsView={false}
                          title={t('Consignee Details')}
                          clientVal={item?.vehicle.client ? item?.vehicle.client : null}
                        />
                      </Box>
                    )}
                    {idx < shipmentItems.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>

      {dialogOpen && (
        <AdvancedSearchVehiclesModal
          clientId={
            ['LCL', 'SPL'].includes(formik.values.shipmentMode)
              ? [
                  formik.values?.primaryClient?.id,
                  ...(formik.values?.shipmentClients || []).map(c => c?.id).filter(Boolean)
                ].filter(Boolean)
              : formik.values?.primaryClient?.id || ''
          }
          destinationId={values?.booking?.destination?.id || ''}
          vehicles={vehicles}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSelect={(selected) => {
            const useRootParties = ['FCL', 'LCL'].includes(values?.shipmentMode);
            const normalizedItems = (selected || []).map((vehicle) => {
              // Keep existing id if this vehicle was already in shipmentItems
              const existingItem = shipmentItems.find(item => item?.vehicle?.id === vehicle.id);
              return {
                id: existingItem?.id,
                vehicle,
                consignee: useRootParties ? values?.consignee ?? null : null,
                notifyParty: useRootParties ? values?.notifyParty ?? null : null,
                consigneeDock: useRootParties ? values?.consigneeDock ?? null : null,
                description: values?.primaryDescription ?? 'Used Vehicles',
                billOfLadingFile: existingItem?.billOfLadingFile || [],
              };
            });
            setFieldValue(shipmentItemsPath, normalizedItems, false);
            setDialogOpen(false);
          }}
          listVehiclesNotShowing={listVehiclesNotShowing}
        />
      )}
    </Box>
  );
}

VehiclesInfo.propTypes = {
  disabled: PropTypes.bool,
  basePath: PropTypes.string.isRequired,
  compact: PropTypes.bool,
  title: PropTypes.string,
  index: PropTypes.number.isRequired
};

export default memo(VehiclesInfo);
